import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@material-ui/core';

import { next } from '../../fsm/events';
import piwik from '../../services/analytics/analytics';
import { eventTags, IDENTIFICATION } from '../../services/analytics/tags';
import { usePageView } from '../../hooks/analytics';
import { getSettings } from '../../selectors/settings';
import { getLocale } from '../../selectors/language';
import { getPhoneCountry } from '../../selectors/eligibility';
import { getErrorCode } from '../../selectors/error';
import ducks from '../../ducks/ducks';

import Button from '../../components/Button/Button';
import Footer from '../../components/Footer/Footer';
import Page from '../../components/Page/Page';
import Title from '../../components/Title/Title';
import TextPicker from '../../components/Inputs/TextPicker/TextPicker';
import PhonePicker from '../../components/Inputs/PhonePicker/PhonePicker';
import GoogleRecaptchaNotice from '../../components/GoogleRecaptchaNotice/GoogleRecaptchaNotice';
import Typography from '../../components/Typography/Typography';
import Modal from '../../components/Modal/Modal';

import messages from './messages';
import { getPhoneCountryPreferences } from '../../utils/settings';
import { validateCarPlate, validatePhoneNumber } from '../../utils/validation';
import { ERROR_CODES } from '../../enums';

const Identification = () => {
  usePageView(IDENTIFICATION);
  const [isVerificationIssueModal, setVerificationIssueModal] = useState(false);

  const dispatch = useDispatch();
  const handleNext = payload => dispatch(next(payload));
  const settings = useSelector(getSettings);
  const local = useSelector(getLocale);
  const phoneCountry = useSelector(getPhoneCountry);
  const errorCode = useSelector(getErrorCode);

  useEffect(() => {
    setVerificationIssueModal([ERROR_CODES.VERIFICATION_ERROR, ERROR_CODES.INTERNAL_SERVER_ERROR].includes(errorCode));
  }, [errorCode]);

  const { preferredCountries, defaultPhoneCountry } = getPhoneCountryPreferences(settings, local);

  const formik = useFormik({
    initialValues: {
      plateNumber: '',
      phoneCountry: phoneCountry || defaultPhoneCountry,
      phoneNumber: '',
    },
    onSubmit: values => {
      const submittedValues = { ...values };
      handleNext({
        ...submittedValues,
        phoneNumber: submittedValues.phoneNumber.replace(/ /g, ''),
      });
    },
    validate: valuesToValidate => {
      const errors = {};

      errors.plateNumber = validateCarPlate(valuesToValidate.plateNumber);
      errors.phoneNumber = validatePhoneNumber(valuesToValidate.phoneCountry)(valuesToValidate.phoneNumber);

      // eslint-disable-next-line eqeqeq
      return Object.fromEntries(Object.entries(errors).filter(([, value]) => value != null));
    },
  });

  useEffect(() => {
    formik.validateForm();
  }, []); // eslint-disable-line

  return (
    <Page name="identification" direction="column">
      <Box style={{ flex: 1 }}>
        <Title content={messages.title} src="identification" />
        <Typography content={messages.subtitle} color="secondary" />
        <form onSubmit={formik.handleSubmit} style={{ marginTop: 22 }}>
          <TextPicker id="plateNumber" formik={formik} label={messages.formPlateNumber} required emptyIconBoxNeeded={false} />
          <PhonePicker
            id="phoneNumber"
            formik={formik}
            country={formik.values.phoneCountry}
            preferredCountries={preferredCountries}
            label={messages.formPhoneNumber}
            required
            iconHidden
            shrink={false}
          />
        </form>
      </Box>
      <Footer>
        <Box>
          <Button
            id="identification-continue-button"
            testId="identification-continue-button"
            name="identification-continue-button"
            content={messages.continue}
            disabled={!formik.isValid}
            onClick={() => {
              piwik.trackEvent(eventTags.identification_continue);
              formik.submitForm();
            }}
          />
          <GoogleRecaptchaNotice />
        </Box>
      </Footer>
      <Modal
        id="identification-verification-modal"
        testId="identification-verification-modal"
        actions={[
          {
            content: messages.verificationCheck,
            name: 'check',
            onClick: () => {
              piwik.trackEvent(eventTags.identification_check_information);
              setVerificationIssueModal(false);
              dispatch(ducks.error.actions.clearTechnicalError());
            },
          },
        ]}
        content={messages.verificationIssueText}
        open={isVerificationIssueModal}
        title={{
          alt: 'verificationIssueTitle',
          content: messages.verificationIssueTitle,
          src: 'verificationIssue',
        }}
        customStyle={{
          dialogTitle: {
            color: 'black',
          },
          color: 'grey',
        }}
      />
    </Page>
  );
};

export default Identification;
